/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit'
import { c1UserTimeZoneSelector } from '@redux/slices/common'
import stackApi, {
  resetScannerStacksCacheAction,
  resetStorageStacksCacheAction,
} from '@redux/apis/stack'
import { getFSSTimeDisplayFormat, awaitTimeout } from '@utils/time'
import clientStorage from '@utils/localStorage'
import INPUT_ERROR from '@constants/form'
import { AWS_DEPLOY_INPUT_KEYS } from '@constants/aws'
import { AZURE_DEPLOY_INPUT_KEYS } from '@constants/azure'
import { ACTIVE_PROVIDER } from '@constants/localStorageKey'
import { GCP_DEPLOY_INPUT_KEYS, GCP_OIDC_INPUT_KEYS } from '@constants/gcp'
import {
  RETRIEVE_SCANNER_STACK_FAILED_MSG,
  RETRIEVE_STORAGE_STACK_FAILED_MSG,
  STACK_ALREADY_DEPLOYED_MSG,
  UPDATE_AZURE_LICENSE_FAILED_MSG,
  SCANNER_IS_UPDATING_BY_OTHERS,
} from '@constants/stackStatus'
import { QueryStatus } from '@reduxjs/toolkit/dist/query/core/apiState'

const DEPLOY_INPUT_KEYS = [
  ...AWS_DEPLOY_INPUT_KEYS,
  ...AZURE_DEPLOY_INPUT_KEYS,
  ...GCP_DEPLOY_INPUT_KEYS,
  ...GCP_OIDC_INPUT_KEYS,
]

export const defaultStackMeta: Redux.StackListMeta = {
  sortBy: 'name',
  sortOrder: 'asc',
  cursor: null,
}

export const REDUCER_NAME = 'deployment'

export const initDeploymentState: Redux.DeploymentState = {
  isLoading: false,
  scannerStacks: {
    aws: [],
    azure: [],
    gcp: [],
  },
  scannerMeta: {
    aws: defaultStackMeta,
    azure: defaultStackMeta,
    gcp: defaultStackMeta,
  },
  storageStacks: {
    aws: [],
    azure: [],
    gcp: [],
  },
  storageMeta: {
    aws: defaultStackMeta,
    azure: defaultStackMeta,
    gcp: defaultStackMeta,
  },
  selectedScanner: {
    aws: null,
    azure: null,
    gcp: null,
  },
  selectedStorage: {
    aws: null,
    azure: null,
    gcp: null,
  },
  isScannerFetching: false,
  isStorageFetching: false,
  hasScannerErrorWhenFetchMore: false,
  hasStorageErrorWhenFetchMore: false,
  activeProvider: null,
  activeProviderUppercase: null,
  deployForm: DEPLOY_INPUT_KEYS.reduce(
    (acc, cur) => ({ ...acc, [cur]: '' }),
    {} as Redux.DeployFormInputs
  ),
  deployFormError: DEPLOY_INPUT_KEYS.reduce(
    (acc, cur) => ({ ...acc, [cur]: INPUT_ERROR.NONE }),
    {} as Redux.DeployFormInputErrors
  ),
  alertMsg: null,
  alertIcon: 'success',
  alertOpen: false,
  isGCPDeprecatedStackType: false,
  multipleDeploymentTotalCount: 0,
  multipleDeploymentScanners: [],
  multipleDeploymentStorages: [],
  multipleDeploymentFailureScanners: [],
  multipleDeploymentFailureStorages: [],
}

export const fetchScannerStacks = createAsyncThunk<
  {
    data: Response.Stack.ScannerListAPI
    status: QueryStatus
  },
  Omit<Request.StackListParams, 'type'>
>(
  `${REDUCER_NAME}/fetchScannerStacks`,
  async (
    { delay, shouldResetStackListCache, ...params },
    { dispatch, getState }
  ) => {
    const {
      deployment: { activeProvider, scannerMeta },
    } = getState() as Redux.RootState
    let forceRefetch = false

    if (delay >= 0 || shouldResetStackListCache) {
      if (delay) await awaitTimeout(delay)

      forceRefetch = true
    }

    const { data, status } = await dispatch(
      stackApi.endpoints.getScannerStacks.initiate(
        {
          ...params,
          provider: params.provider || activeProvider,
          sortOrder: scannerMeta[activeProvider].sortOrder,
        },
        {
          forceRefetch,
        }
      )
    )

    return {
      data,
      status,
    }
  }
)

export const fetchStorageStacks = createAsyncThunk<
  {
    data: Response.Stack.StorageListAPI
    status: QueryStatus
  },
  Omit<Request.StorageStackListParams, 'type'>
>(
  `${REDUCER_NAME}/fetchStorageStacks`,
  async (
    { delay, shouldResetStackListCache, ...params },
    { dispatch, getState }
  ) => {
    const {
      deployment: { activeProvider },
    } = getState() as Redux.RootState
    let forceRefetch = false

    if (delay >= 0 || shouldResetStackListCache) {
      if (delay) await awaitTimeout(delay)

      forceRefetch = true
    }

    const { data, status } = await dispatch(
      stackApi.endpoints.getStorageStacks.initiate(
        {
          ...params,
          provider: params.provider || activeProvider,
        },
        {
          forceRefetch,
        }
      )
    )

    return {
      data,
      status,
    }
  }
)

export const createStack = createAsyncThunk<
  Response.Stack.CreateStackResponseBody,
  Request.CreateStackAPIParam
>(`${REDUCER_NAME}/createStack`, async (params, { dispatch }) => {
  try {
    const data = await dispatch(
      stackApi.endpoints.postStack.initiate(params)
    ).unwrap()

    return {
      stackID: data?.stackID,
      errorCode: null,
    }
  } catch (err) {
    const { status } = err

    console.error(err)
    return {
      stackID: null,
      errorCode: status,
    }
  }
})

// regard 404 as still creating
const backoffBase = 2
const maxAttempt = 10
const resetCount = 5

const fetchStackExponentialBackoff = (
  method: () => Promise<Response.CustomResponse<Response.Stack.AnyStack>>,
  attempt = 0
): Promise<Response.Stack.PollStackCreationStatus<Response.Stack.AnyStack>> =>
  new Promise((resolve, reject) => {
    const pollStackCreationStatus = () =>
      fetchStackExponentialBackoff(method, ++attempt)
        .then((value: any) => resolve(value))
        .catch((err: any) => reject(err))
    setTimeout(async () => {
      try {
        const { data, status } = await method()
        const isLoading = data?.status === 'creating'

        if (isLoading) {
          pollStackCreationStatus()
        } else {
          resolve({
            stack: data,
            statusCode: data?.status,
            responseStatus: status,
          })
        }
      } catch (err) {
        const { status, data } = err

        const is404 = status === 404
        const isNotFound = data?.message === 'not found'

        if ((is404 || isNotFound) && attempt < maxAttempt) {
          pollStackCreationStatus()
        } else {
          reject(new Error(`${status}`))
        }
      }
    }, backoffBase ** (attempt % resetCount) * 1000)
  })

export const fetchStackStatus = createAsyncThunk<
  Response.Stack.PollStackCreationStatus<Response.Stack.AnyStack>,
  {
    stackID: string
    type: Response.Stack.StackType
    scannerStack?: string
    shouldResetStackListCache?: boolean
  }
>(
  `${REDUCER_NAME}/fetchStack`,
  async (
    { stackID, type, scannerStack, shouldResetStackListCache },
    { dispatch, getState }
  ) => {
    const {
      deployment: { activeProvider },
    } = getState() as Redux.RootState
    let statusCode: Response.Stack.StackItemStatus

    try {
      const res = await fetchStackExponentialBackoff(async () => {
        const data = await dispatch(
          stackApi.endpoints.getStack.initiate(stackID, {
            forceRefetch: true,
          })
        ).unwrap()

        return data
      })

      if (res.stack.status === 'ok') {
        statusCode = res.stack.status

        if (shouldResetStackListCache) {
          if (type === 'scanner') {
            dispatch(resetScannerStacksCacheAction(activeProvider))
          } else {
            dispatch(
              resetStorageStacksCacheAction(activeProvider, scannerStack)
            )
          }
        }
      } else if (res.stack.status === 'creation-failed') {
        const { statusDetail } = res.stack

        if (
          statusDetail === RETRIEVE_SCANNER_STACK_FAILED_MSG ||
          statusDetail === RETRIEVE_STORAGE_STACK_FAILED_MSG
        ) {
          statusCode = 'retrieve-stack-failed'
        } else if (statusDetail === STACK_ALREADY_DEPLOYED_MSG) {
          statusCode = 'stack-already-deployed'
        } else if (statusDetail === UPDATE_AZURE_LICENSE_FAILED_MSG) {
          statusCode = 'update-azure-license-failed'
        } else if (statusDetail === SCANNER_IS_UPDATING_BY_OTHERS) {
          statusCode = 'scanner-is-updating-by-others'
        }
      }

      const result: Response.Stack.PollStackCreationStatus<Response.Stack.AnyStack> =
        {
          stack: res.stack,
          statusCode,
          responseStatus: res?.responseStatus,
        }

      return result
    } catch (err) {
      const responseStatus = err.message

      if (shouldResetStackListCache) {
        if (type === 'scanner') {
          dispatch(resetScannerStacksCacheAction(activeProvider))
        } else {
          dispatch(resetStorageStacksCacheAction(activeProvider, scannerStack))
        }
      }

      return {
        stack: null,
        statusCode: responseStatus,
        responseStatus,
      }
    }
  }
)

function setSelectedScannerAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Response.Stack.ScannerStack>
) {
  state.selectedScanner[action.payload?.provider || state.activeProvider] =
    action.payload
}

function setSelectedStorageAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Response.Stack.StorageStack>
) {
  state.selectedStorage[action.payload?.provider || state.activeProvider] =
    action.payload
}

function resetDeployFormAction(state: typeof initDeploymentState) {
  state.deployForm = initDeploymentState.deployForm
  state.deployFormError = initDeploymentState.deployFormError
}

function resetScannerStacksAction(state: typeof initDeploymentState) {
  if (state.activeProvider === 'gcp') {
    state.scannerStacks.gcp = initDeploymentState.scannerStacks.gcp
    state.scannerMeta.gcp = initDeploymentState.scannerMeta.gcp
  } else {
    state.scannerStacks.aws = initDeploymentState.scannerStacks.aws
    state.scannerMeta.aws = initDeploymentState.scannerMeta.aws
    state.scannerStacks.azure = initDeploymentState.scannerStacks.azure
    state.scannerMeta.azure = initDeploymentState.scannerMeta.azure
  }
  // Resume after the old GCP stack is deleted.
  // state.scannerStacks = initDeploymentState.scannerStacks
  // state.scannerMeta = initDeploymentState.scannerMeta
  state.isScannerFetching = false
  state.hasScannerErrorWhenFetchMore = false
}

function setScannerMetaAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Redux.StackListMeta>
) {
  state.scannerMeta = {
    ...state.scannerMeta,
    [state.activeProvider]: {
      ...state.scannerMeta[state.activeProvider],
      ...action.payload,
    },
  }
}

function resetStorageStacksAction(state: typeof initDeploymentState) {
  state.storageStacks = initDeploymentState.storageStacks
  state.storageMeta = initDeploymentState.storageMeta
  state.isStorageFetching = false
  state.hasStorageErrorWhenFetchMore = false
}

function setStorageMetaAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Redux.StackListMeta>
) {
  state.storageMeta = {
    ...state.storageMeta,
    [state.activeProvider]: action.payload,
  }
}

function setActiveProviderAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Response.Stack.StackItemProvider>
) {
  clientStorage.set(ACTIVE_PROVIDER, action.payload)

  state.activeProvider = action.payload
  state.activeProviderUppercase =
    action.payload.toUpperCase() as Redux.ProviderTypeUppercase
}

function setDeployFormInputAction(
  state: typeof initDeploymentState,
  action: PayloadAction<{ key: Redux.DeployStackInputKeyName; val: string }>
) {
  const isValid = DEPLOY_INPUT_KEYS.some(k => k === action.payload.key)

  if (isValid) {
    state.deployForm[action.payload.key] = action.payload.val
  }
}

function setDeployFormInputErrorAction(
  state: typeof initDeploymentState,
  action: PayloadAction<{
    key: Redux.DeployStackInputKeyName
    val: FSSModal.FSSErrorType
  }>
) {
  const isValid = DEPLOY_INPUT_KEYS.some(k => k === action.payload.key)

  if (isValid) {
    state.deployFormError[action.payload.key] = action.payload.val
  }
}

function setOIDConnectAlertMsgAction(
  state: typeof initDeploymentState,
  action: PayloadAction<{
    msg: string
    icon: Redux.AlertIcon
    open: boolean
  }>
) {
  state.alertMsg = action.payload.msg
  state.alertIcon = action.payload.icon
  state.alertOpen = action.payload.open
}

function setIsLoadingAction(
  state: typeof initDeploymentState,
  action: PayloadAction<boolean>
) {
  state.isLoading = action.payload
}

function resetDeploymentStateAction() {
  return initDeploymentState
}

function handleFetchScannerStacksFullfilledActionHelper(
  state: typeof initDeploymentState,
  payload: Response.Stack.ScannerListAPI,
  params: Omit<Request.StackListParams, 'type'>
) {
  if (!payload) return

  const { stacks, next } = payload
  const {
    provider = state.activeProvider,
    sortBy = state.scannerMeta[state.activeProvider].sortBy,
    sortOrder = state.scannerMeta[state.activeProvider].sortOrder,
    cursor = state.scannerMeta[state.activeProvider].cursor,
  } = params

  state.scannerStacks[provider] = cursor
    ? [...state.scannerStacks[provider], ...stacks]
    : stacks || []
  state.scannerMeta[provider].cursor = next
  state.scannerMeta[provider].sortBy = sortBy
  state.scannerMeta[provider].sortOrder = sortOrder

  state.isScannerFetching = false
}

function handleFetchStorageStacksFullfilledActionHelper(
  state: typeof initDeploymentState,
  payload: Response.Stack.StorageListAPI,
  params: Omit<Request.StorageStackListParams, 'type'>
) {
  const { stacks, next } = payload
  const {
    provider = state.activeProvider,
    sortBy = state.storageMeta[state.activeProvider].sortBy,
    sortOrder = state.storageMeta[state.activeProvider].sortOrder,
    cursor = state.storageMeta[state.activeProvider].cursor,
  } = params

  state.storageStacks[provider] = cursor
    ? [...state.storageStacks[provider], ...stacks]
    : stacks || []
  state.storageMeta[provider].cursor = next
  state.storageMeta[provider].sortBy = sortBy
  state.storageMeta[provider].sortOrder = sortOrder

  state.isStorageFetching = false
}

function setScannerStacksAction(
  state: typeof initDeploymentState,
  action: PayloadAction<{
    provider: Response.Stack.StackItemProvider
    stacks: Response.Stack.ScannerStack[]
  }>
) {
  state.scannerStacks[action.payload.provider] = action.payload.stacks
}

function setHasScannerErrorWhenFetchMoreAction(
  state: typeof initDeploymentState,
  action: PayloadAction<boolean>
) {
  state.hasScannerErrorWhenFetchMore = action.payload
}

function setIsScannerFetchingAction(
  state: typeof initDeploymentState,
  action: PayloadAction<boolean>
) {
  state.isScannerFetching = action.payload
}

function setIsStorageFetchingAction(
  state: typeof initDeploymentState,
  action: PayloadAction<boolean>
) {
  state.isStorageFetching = action.payload
}

// The code below is temporary until the clients are updated to use the new GCP stacks
function setIsGCPDeprecatedStackTypeAction(
  state: typeof initDeploymentState,
  action: PayloadAction<boolean>
) {
  state.isGCPDeprecatedStackType = action.payload
}
// The code above is temporary until the clients are updated to use the new GCP stacks

function setMultipleDeploymentTotalCountAction(
  state: typeof initDeploymentState,
  action: PayloadAction<number>
) {
  state.multipleDeploymentTotalCount = action.payload
}

function setMultipleDeploymentScannerStacksAction(
  state: typeof initDeploymentState,
  action: PayloadAction<
    Response.Stack.PollStackCreationStatus<
      Response.Stack.ScannerStack<Response.Stack.GCPStackItemDetails>
    >
  >
) {
  state.multipleDeploymentScanners = action.payload
    ? [...state.multipleDeploymentScanners, action.payload]
    : []
}

function setMultipleDeploymentFailureScannersAction(
  state: typeof initDeploymentState,
  action: PayloadAction<
    Response.Stack.PollStackCreationStatus<
      Response.Stack.ScannerStack<Response.Stack.GCPStackItemDetails>
    >
  >
) {
  state.multipleDeploymentFailureScanners = action.payload
    ? [...state.multipleDeploymentFailureScanners, action.payload]
    : []
}

function setMultipleDeploymentStorageStacksAction(
  state: typeof initDeploymentState,
  action: PayloadAction<
    Response.Stack.PollStackCreationStatus<
      Response.Stack.StorageStack<Response.Stack.GCPStorageStackItemDetails>
    >
  >
) {
  state.multipleDeploymentStorages = action.payload
    ? [...state.multipleDeploymentStorages, action.payload]
    : []
}

function setMultipleDeploymentFailureStoragesAction(
  state: typeof initDeploymentState,
  action: PayloadAction<Redux.FailureMultipleDeploymentStorage>
) {
  state.multipleDeploymentFailureStorages = action.payload
    ? [...state.multipleDeploymentFailureStorages, action.payload]
    : []
}

function resetMultipleDeploymentStateAction(state: typeof initDeploymentState) {
  state.multipleDeploymentTotalCount = 0
  state.multipleDeploymentScanners = []
  state.multipleDeploymentFailureScanners = []
  state.multipleDeploymentStorages = []
  state.multipleDeploymentFailureStorages = []
}

export const deploymentSlice = createSlice({
  name: REDUCER_NAME,
  initialState: initDeploymentState,
  reducers: {
    setSelectedScanner: setSelectedScannerAction,
    setSelectedStorage: setSelectedStorageAction,
    resetDeployForm: resetDeployFormAction,
    resetScannerStacks: resetScannerStacksAction,
    setScannerMeta: setScannerMetaAction,
    resetStorageStacks: resetStorageStacksAction,
    setStorageMeta: setStorageMetaAction,
    setActiveProvider: setActiveProviderAction,
    setDeployFormInput: setDeployFormInputAction,
    setDeployFormInputError: setDeployFormInputErrorAction,
    setOIDConnectAlertMsg: setOIDConnectAlertMsgAction,
    setIsLoading: setIsLoadingAction,
    resetDeployment: resetDeploymentStateAction,
    setScannerStacks: setScannerStacksAction,
    setIsScannerFetching: setIsScannerFetchingAction,
    setIsStorageFetching: setIsStorageFetchingAction,
    // The code below is temporary until the clients are updated to use the new GCP stacks
    setIsGCPDeprecatedStackType: setIsGCPDeprecatedStackTypeAction,
    // The code above is temporary until the clients are updated to use the new GCP stacks
    setMultipleDeploymentTotalCount: setMultipleDeploymentTotalCountAction,
    setMultipleDeploymentScannerStacks:
      setMultipleDeploymentScannerStacksAction,
    setMultipleDeploymentStorageStacks:
      setMultipleDeploymentStorageStacksAction,
    setMultipleDeploymentFailureScanners:
      setMultipleDeploymentFailureScannersAction,
    setMultipleDeploymentFailureStorages:
      setMultipleDeploymentFailureStoragesAction,
    resetMultipleDeploymentState: resetMultipleDeploymentStateAction,
    setHasScannerErrorWhenFetchMore: setHasScannerErrorWhenFetchMoreAction,
  },
  extraReducers: builder => {
    builder
      // fetchScannerStacks.fulfilled && fetchStorageStacks.fulfilled are handling the cache data or the API response from stackApi
      .addCase(fetchScannerStacks.pending, (state, action) => {
        const { sortBy, sortOrder, cursor, provider } = action.meta.arg
        const targetProvider = provider || state.activeProvider

        state.isScannerFetching = true
        state.hasScannerErrorWhenFetchMore = false

        if (targetProvider) {
          state.scannerMeta[targetProvider].sortBy = sortBy
          state.scannerMeta[targetProvider].sortOrder = sortOrder
          state.scannerMeta[targetProvider].cursor = cursor
        }
      })
      .addCase(fetchScannerStacks.rejected, state => {
        state.hasScannerErrorWhenFetchMore = true
        state.isScannerFetching = false
      })
      .addCase(fetchScannerStacks.fulfilled, (state, action) => {
        const { requestStatus } = action.meta

        if (requestStatus === 'fulfilled') {
          handleFetchScannerStacksFullfilledActionHelper(
            state,
            action.payload.data,
            action.meta.arg
          )
        }
      })
      .addCase(fetchStorageStacks.pending, (state, action) => {
        const { sortBy, sortOrder, cursor, provider } = action.meta.arg
        const targetProvider = provider || state.activeProvider

        state.isStorageFetching = true
        state.hasStorageErrorWhenFetchMore = false

        if (targetProvider) {
          state.storageMeta[targetProvider].sortBy = sortBy
          state.storageMeta[targetProvider].sortOrder = sortOrder
          state.storageMeta[targetProvider].cursor = cursor
        }
      })
      .addCase(fetchStorageStacks.rejected, state => {
        state.hasStorageErrorWhenFetchMore = true
        state.isStorageFetching = false
      })
      .addCase(fetchStorageStacks.fulfilled, (state, action) => {
        const {
          arg: { scannerStack },
          requestStatus,
        } = action.meta

        if (
          scannerStack ===
            state.selectedScanner[state.activeProvider]?.stackID &&
          requestStatus === 'fulfilled'
        ) {
          handleFetchStorageStacksFullfilledActionHelper(
            state,
            action.payload.data,
            action.meta.arg
          )
        }
      })
      .addCase(createStack.pending, state => {
        state.isLoading = true
      })
      .addCase(createStack.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(createStack.rejected, state => {
        state.isLoading = false
      })
      .addCase(fetchStackStatus.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchStackStatus.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(fetchStackStatus.rejected, state => {
        state.isLoading = false
      })
      .addMatcher(stackApi.endpoints.deleteStack.matchPending, state => {
        state.isLoading = true
      })
      .addMatcher(stackApi.endpoints.deleteStack.matchFulfilled, state => {
        state.isLoading = false
      })
      .addMatcher(stackApi.endpoints.deleteStack.matchRejected, state => {
        state.isLoading = false
      })
  },
})

const selectSelf = (state: Redux.RootState) => state.deployment

export const selectedScannerSelector = createSelector(
  selectSelf,
  deployment => deployment.selectedScanner
)

export const selectedStorageSelector = createSelector(
  selectSelf,
  deployment => deployment.selectedStorage
)

export const activeProviderSelector = createSelector(
  selectSelf,
  deployment => deployment.activeProvider
)

const getStorageStacksHelper = <T>(
  stacks: any[] = [],
  getC1UserTzDateInstance: (date: string) => Date,
  c1UserTimeZoneOffsetByHr: string
): T[] =>
  stacks.map(data => {
    const createdDateStr = data.created || ''
    const date = getC1UserTzDateInstance(createdDateStr)

    return {
      ...data,
      created: createdDateStr
        ? getFSSTimeDisplayFormat(date, c1UserTimeZoneOffsetByHr)
        : null,
    }
  })

const getScannerStacksHelper = <T>(
  stacks: any[] = [],
  getC1UserTzDateInstance: (date: string) => Date,
  c1UserTimeZoneOffsetByHr: string,
  activeProvider?: Response.Stack.StackItemProvider,
  isGCPDeprecatedStackType?: boolean
): T[] =>
  stacks.reduce((acc, data) => {
    const createdDateStr = data.created || ''
    const date = getC1UserTzDateInstance(createdDateStr)
    const stack = {
      ...data,
      created: createdDateStr
        ? getFSSTimeDisplayFormat(date, c1UserTimeZoneOffsetByHr)
        : null,
    }

    if (
      activeProvider === 'gcp' &&
      isGCPDeprecatedStackType &&
      (data?.details.isLegacyDeployment === true ||
        data?.details.isLegacyDeployment === undefined)
    ) {
      acc.push(stack)
    }

    if (
      activeProvider === 'gcp' &&
      !isGCPDeprecatedStackType &&
      data?.details.isLegacyDeployment === false
    ) {
      acc.push(stack)
    }

    if (activeProvider === 'aws' || activeProvider === 'azure') {
      acc.push(stack)
    }

    return acc
  }, [])

// The code below is temporary until the clients are updated to use the new GCP stacks
export const isGCPDeprecatedStackTypeSelector = createSelector(
  selectSelf,
  deployment => deployment.isGCPDeprecatedStackType
)
// The code above is temporary until the clients are updated to use the new GCP stacks

const selectScannerStacks = createSelector(
  selectSelf,
  activeProviderSelector,
  (deployment, activeProvider) => deployment.scannerStacks[activeProvider]
)

export const scannerStacksSelector = createSelector(
  selectScannerStacks,
  c1UserTimeZoneSelector,
  activeProviderSelector,
  isGCPDeprecatedStackTypeSelector,
  (
    scanners,
    { getC1UserTzDateInstance, c1UserTimeZoneOffsetByHr },
    activeProviderSelector,
    isGCPDeprecatedStackType
  ) =>
    getScannerStacksHelper<Response.Stack.ScannerStack>(
      scanners,
      getC1UserTzDateInstance,
      c1UserTimeZoneOffsetByHr,
      activeProviderSelector,
      isGCPDeprecatedStackType
    )
)

const selectStorageStacks = createSelector(
  selectSelf,
  activeProviderSelector,
  (deployment, activeProvider) => deployment.storageStacks[activeProvider]
)

export const storageStacksSelector = createSelector(
  selectStorageStacks,
  c1UserTimeZoneSelector,
  (storages, { getC1UserTzDateInstance, c1UserTimeZoneOffsetByHr }) =>
    getStorageStacksHelper<Response.Stack.StorageStack>(
      storages,
      getC1UserTzDateInstance,
      c1UserTimeZoneOffsetByHr
    )
)

export const isScannerFetchingSelector = createSelector(
  selectSelf,
  deployment => deployment.isScannerFetching
)

export const isStorageFetchingSelector = createSelector(
  selectSelf,
  deployment => deployment.isStorageFetching
)

export const hasScannerErrorWhenFetchMoreSelector = createSelector(
  selectSelf,
  deployment => deployment.hasScannerErrorWhenFetchMore
)

export const hasStorageErrorWhenFetchMoreSelector = createSelector(
  selectSelf,
  deployment => deployment.hasStorageErrorWhenFetchMore
)

export const scannerMetaSelector = createSelector(
  selectSelf,
  deployment => deployment.scannerMeta
)

export const storageMetaSelector = createSelector(
  selectSelf,
  deployment => deployment.storageMeta
)

export const activeProviderUppercaseSelector = createSelector(
  selectSelf,
  deployment => deployment.activeProviderUppercase
)

export const activeProviderAndUppercaseNameSelector = createSelector(
  activeProviderSelector,
  activeProviderUppercaseSelector,
  (activeProvider, activeProviderUppercase) => ({
    activeProvider,
    activeProviderUppercase,
  })
)

export const isLoadingSelector = createSelector(
  selectSelf,
  deployment => deployment.isLoading
)

export const deployFormSelector = createSelector(
  selectSelf,
  deployment => deployment.deployForm
)

export const deployFormErrorSelector = createSelector(
  selectSelf,
  deployment => deployment.deployFormError
)

export const awsScannerStackManagementRoleInputSelector = createSelector(
  deployFormSelector,
  ({ awsScannerStackManagementRole }) => awsScannerStackManagementRole
)

export const awsScannerStackManagementRoleInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ awsScannerStackManagementRole }) => awsScannerStackManagementRole
)

export const awsStorageStackManagementRoleInputSelector = createSelector(
  deployFormSelector,
  ({ awsStorageStackManagementRole }) => awsStorageStackManagementRole
)

export const awsStorageStackManagementRoleInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ awsStorageStackManagementRole }) => awsStorageStackManagementRole
)

export const azureScannerStackTenantIdInputSelector = createSelector(
  deployFormSelector,
  ({ azureScannerStackTenantId }) => azureScannerStackTenantId
)

export const azureScannerStackTenantIdInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ azureScannerStackTenantId }) => azureScannerStackTenantId
)

export const azureScannerStackResourceGroupIdInputSelector = createSelector(
  deployFormSelector,
  ({ azureScannerStackResourceGroupId }) => azureScannerStackResourceGroupId
)

export const azureScannerStackResourceGroupIdInputErrorSelector =
  createSelector(
    deployFormErrorSelector,
    ({ azureScannerStackResourceGroupId }) => azureScannerStackResourceGroupId
  )

export const azureStorageStackTenantIdInputSelector = createSelector(
  deployFormSelector,
  ({ azureStorageStackTenantId }) => azureStorageStackTenantId
)

export const azureStorageStackTenantIdInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ azureStorageStackTenantId }) => azureStorageStackTenantId
)

export const azureStorageStackResourceGroupIdInputSelector = createSelector(
  deployFormSelector,
  ({ azureStorageStackResourceGroupId }) => azureStorageStackResourceGroupId
)

export const azureStorageStackResourceGroupIdInputErrorSelector =
  createSelector(
    deployFormErrorSelector,
    ({ azureStorageStackResourceGroupId }) => azureStorageStackResourceGroupId
  )

export const gcpAIOTerraformJSONInputSelector = createSelector(
  deployFormSelector,
  ({ gcpAIOTerraformJSON }) => gcpAIOTerraformJSON
)

export const gcpAIOTerraformJSONInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ gcpAIOTerraformJSON }) => gcpAIOTerraformJSON
)

export const gcpScannerTerraformJSONInputSelector = createSelector(
  deployFormSelector,
  ({ gcpScannerTerraformJSON }) => gcpScannerTerraformJSON
)

export const gcpScannerTerraformJSONInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ gcpScannerTerraformJSON }) => gcpScannerTerraformJSON
)

export const gcpStorageTerraformJSONInputSelector = createSelector(
  deployFormSelector,
  ({ gcpStorageTerraformJSON }) => gcpStorageTerraformJSON
)

export const gcpStorageTerraformJSONInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ gcpStorageTerraformJSON }) => gcpStorageTerraformJSON
)

export const OIDConnectGCPAccountInputSelector = createSelector(
  deployFormSelector,
  ({ gcpOIDConnectProjectID }) => gcpOIDConnectProjectID
)

export const OIDConnectGCPTerraformJSONInputSelector = createSelector(
  deployFormSelector,
  ({ gcpOIDConnectTerraformJSON }) => gcpOIDConnectTerraformJSON
)

export const OIDConnectGCPTerraformJSONInputErrorSelector = createSelector(
  deployFormErrorSelector,
  ({ gcpOIDConnectTerraformJSON }) => gcpOIDConnectTerraformJSON
)

export const OIDConnectAlertMsgSelector = createSelector(
  selectSelf,
  deployment => deployment.alertMsg
)

export const OIDConnectAlertIconSelector = createSelector(
  selectSelf,
  deployment => deployment.alertIcon
)

export const OIDConnectAlertOpenSelector = createSelector(
  selectSelf,
  deployment => deployment.alertOpen
)

export const OIDConnectAlertSelector = createSelector(
  OIDConnectAlertMsgSelector,
  OIDConnectAlertIconSelector,
  OIDConnectAlertOpenSelector,
  (alertMsg, alertIcon, alertOpen) => ({
    alertMsg,
    alertIcon,
    alertOpen,
  })
)

export const multipleDeploymentTotalCountSelector = createSelector(
  selectSelf,
  deployment => deployment.multipleDeploymentTotalCount
)

export const multipleDeploymentScannersSelector = createSelector(
  selectSelf,
  deployment => deployment.multipleDeploymentScanners
)

export const multipleDeploymentStoragesSelector = createSelector(
  selectSelf,
  deployment => deployment.multipleDeploymentStorages
)

export const multipleDeploymentFailureScannersSelector = createSelector(
  selectSelf,
  deployment => deployment.multipleDeploymentFailureScanners
)

export const multipleDeploymentFailureStoragesSelector = createSelector(
  selectSelf,
  deployment => deployment.multipleDeploymentFailureStorages
)

export const {
  setSelectedScanner,
  setSelectedStorage,
  resetDeployForm,
  resetScannerStacks,
  setScannerMeta,
  resetStorageStacks,
  setStorageMeta,
  setActiveProvider,
  setDeployFormInput,
  setDeployFormInputError,
  setOIDConnectAlertMsg,
  setIsLoading,
  resetDeployment,
  setScannerStacks,
  setIsScannerFetching,
  setIsStorageFetching,
  // The code below is temporary until the clients are updated to use the new GCP stacks
  setIsGCPDeprecatedStackType,
  setHasScannerErrorWhenFetchMore,
  // The code above is temporary until the clients are updated to use the new GCP stacks
  setMultipleDeploymentTotalCount,
  setMultipleDeploymentScannerStacks,
  setMultipleDeploymentStorageStacks,
  setMultipleDeploymentFailureScanners,
  setMultipleDeploymentFailureStorages,
  resetMultipleDeploymentState,
} = deploymentSlice.actions

export default deploymentSlice.reducer
